import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, EyeOff, Eye } from "lucide-react";
import { adminListPages, adminUpdatePage } from "../../lib/api";

/**
 * Shows every CMS Page that currently has `show_in_footer = true`, grouped by
 * its `footer_column`. Lets the admin instantly hide one with a single click —
 * because the static column editor only manages hand-typed links; auto-injected
 * page links live on the Page record itself and persist until that flag is
 * unset.
 */
export default function DynamicFooterPagesManager({ onToast }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListPages();
      setPages(data || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Could not load pages", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (page, val) => {
    setBusy((b) => ({ ...b, [page.id]: true }));
    try {
      await adminUpdatePage(page.id, { ...page, show_in_footer: val });
      onToast?.(val ? `"${page.title}" will appear in footer` : `"${page.title}" hidden from footer`);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Update failed", "error");
    } finally {
      setBusy((b) => ({ ...b, [page.id]: false }));
    }
  };

  const inFooter = pages.filter((p) => p.show_in_footer);
  const notInFooter = pages.filter((p) => !p.show_in_footer);

  const grouped = inFooter.reduce((acc, p) => {
    const col = p.footer_column || "(no column)";
    (acc[col] ||= []).push(p);
    return acc;
  }, {});

  return (
    <div className="border border-[#11151C]/10 rounded-xl p-4" data-testid="footer-dynamic-pages-manager">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="font-bold text-[#11151C]">Dynamic pages currently in footer</h3>
          <p className="text-xs text-[#11151C]/55 mt-0.5">
            These come from <b>Admin → Pages</b> (any page with <i>Show in footer</i> enabled). They appear under the
            column that matches their <i>Footer column</i> field. Toggle off to remove from the live footer immediately.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="footer-pages-refresh"
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#11151C]/15 text-xs font-semibold hover:bg-[#FFE6D6]"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {Object.keys(grouped).length === 0 && !loading && (
        <p className="text-xs text-[#11151C]/55 italic mt-3">No dynamic pages are showing in the footer right now.</p>
      )}

      <div className="mt-3 space-y-3">
        {Object.entries(grouped).map(([col, items]) => (
          <div key={col}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/55 mb-1.5">In &quot;{col}&quot;</div>
            <ul className="space-y-1.5">
              {items.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-[#FFFBF6] border border-[#11151C]/10">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-[#11151C] truncate">{p.title}</div>
                    <div className="text-[11px] text-[#11151C]/55 truncate">/{p.slug}</div>
                  </div>
                  <button
                    onClick={() => toggle(p, false)}
                    disabled={!!busy[p.id]}
                    data-testid={`footer-page-hide-${p.id}`}
                    className="inline-flex items-center gap-1 text-xs font-bold text-red-600 hover:bg-red-50 px-2 py-1 rounded disabled:opacity-50"
                  >
                    {busy[p.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <EyeOff className="w-3.5 h-3.5" />} Hide
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {notInFooter.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs font-semibold text-[#11151C]/65">
            Pages NOT in footer ({notInFooter.length}) — show all
          </summary>
          <ul className="mt-2 space-y-1.5">
            {notInFooter.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-2 p-2 rounded-lg border border-dashed border-[#11151C]/10">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-[#11151C] truncate">{p.title}</div>
                  <div className="text-[11px] text-[#11151C]/55 truncate">
                    /{p.slug} {p.footer_column ? `· col: ${p.footer_column}` : "· no column set"}
                  </div>
                </div>
                <button
                  onClick={() => toggle(p, true)}
                  disabled={!!busy[p.id] || !p.footer_column}
                  title={!p.footer_column ? "Set a Footer column on the page first" : "Show in footer"}
                  data-testid={`footer-page-show-${p.id}`}
                  className="inline-flex items-center gap-1 text-xs font-bold text-[#FF6B3D] hover:bg-[#FFE6D6] px-2 py-1 rounded disabled:opacity-40"
                >
                  <Eye className="w-3.5 h-3.5" /> Show
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
